import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subscription, filter } from 'rxjs';
import { take } from 'rxjs/operators';
import { UserInfo, LogStatus, LogError, RunView } from '@memberjunction/core';
import { UserService } from 'src/app/services/user.service';
// import { DataFetchService } from 'src/app/services/data-fetch.service';
import { SummaryCard, DataGridColumn, DataGridExportOptions } from 'src/app/utils/interfaces';
import { MembershipRosterEntity } from 'mj_generatedentities';
import { AcsDataGridComponent } from '../shared/acs-data-grid/acs-data-grid';
import { DropdownOption } from '../shared/acs-button/acs-button';
import { GridExportOptions, MemberRosterGridColumns } from 'src/app/utils/grid-constants';
import { EntityNames } from 'src/app/utils/constants';
import { UserAuthService } from 'src/app/services/user-auth.service';
import { UserCommitteeAccessService } from 'src/app/services/user-committee-access.service';
import { ActiveUserService } from 'src/app/services/active-user.service';

@Component({
  selector: 'acs-member-roster',
  templateUrl: './member-roster.html',
  styleUrls: ['../../../styles/_page-layout.scss', './member-roster-specific.scss']
})
export class MemberRosterComponent implements OnInit, OnDestroy {
  @ViewChild('memberGrid') memberGrid!: AcsDataGridComponent;
  
  private subscription = new Subscription();
  readonly entityName = EntityNames.MembershipRosters;

  isLoading = true;
  error: string | null = null;
  
  // summaryCards: SummaryCard[] = [];
  // summary!: MembershipRosterMetricsEntity;

  // Grid Configuration
  gridColumns: DataGridColumn[] = MemberRosterGridColumns;
  members: MembershipRosterEntity[] = [];
  selectedMembers: MembershipRosterEntity[] = [];
  currentUser!: UserInfo;
  chapterId?: string;

  // Export dropdown options
  exportOptions: DropdownOption[] = GridExportOptions;
  
  constructor(
    private userService: UserService,
    // private dataFetchService: DataFetchService,
    private userAuthService: UserAuthService,
    private activeUserService: ActiveUserService,
    private userCommitteeAccessService: UserCommitteeAccessService
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.userService.getUserComplete$.pipe(filter(complete => complete)).subscribe(() => {
        this.initializeComponent();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  initializeComponent(): void {
    this.currentUser = this.userService.getUserInfo();
    // Check if user has a specific chapter assignment
    this.checkUserChapterAccess();
    this.loadInitialData();
  }

  private checkUserChapterAccess(): void {
    // In a real implementation, this would check user's chapter access
    // For now, we'll allow viewing all members
    this.chapterId = undefined;
  }

  private async loadInitialData(): Promise<void> {
    // this.loadSummaryData();
    await this.loadTotalMembersCount();
    this.isLoading = false;
  }

  onGridSelectionChange(selectedMembers: MembershipRosterEntity[]): void {
    this.selectedMembers = selectedMembers;
  }

  onServerError(error: any): void {
    LogError('Error loading members:', undefined, error);
    this.error = 'Failed to load member data. Please try again.';
  }

  onExportOptionSelected(option: DropdownOption): void {
    const format = option.value as 'csv' | 'excel' | 'pdf';
    
    // Use the memberGrid to perform the export
    if (this.memberGrid) {
      this.memberGrid.onExport(format);
      const exportMessage = this.selectedMembers.length > 0 
        ? `Exporting ${this.selectedMembers.length} selected members as ${format.toUpperCase()}`
        : `Exporting all members as ${format.toUpperCase()}`;
      LogStatus(exportMessage, undefined, this.selectedMembers);
    } else {
      alert('Grid not available for export. Please try again.');
    }
  }

  clearSelection(): void {
    this.selectedMembers = [];
  }

  private _totalMembersCount: number = 0;
  // Helper methods for template
  get TotalMembers(): number {
    return this._totalMembersCount;
  }

  get selectedCount(): number {
    return this.selectedMembers.length;
  }

  
  get userRole(): string | null {
    // Get role from UserAuthService which fetches from API
    return this.userAuthService.getUserRole();
  }

  private async loadTotalMembersCount(): Promise<void> {
    // Wait for committee access to load before building the filter
    await new Promise<void>((resolve) => {
      this.subscription.add(
        this.userCommitteeAccessService.isLoading$.pipe(
          filter(isLoading => !isLoading), // Wait until committee access is not loading
          take(1) // Only take the first emission
        ).subscribe(() => {
          LogStatus('MemberRoster: Committee access loaded, now loading total members count');
          resolve();
        })
      );
    });

    const rv = new RunView()
    const result = await rv.RunView<MembershipRosterEntity>({
      EntityName: this.entityName,
      ResultType: 'count_only',
      ExtraFilter: this.buildCommitteeAccessFilter()
    });

    this._totalMembersCount = result.TotalRowCount || 0;
  }

  /*
  private async loadSummaryData(): Promise<void> {
    // Initialize summary data - will be updated when data loads
    const result = await this.dataFetchService.fetchWithParams<MembershipRosterMetricsEntity>({
      EntityName: EntityNames.MembershipRosterMetrics,
      OrderBy: 'MetricDate DESC',
      MaxRows: 1,
      IgnoreMaxRows: false,
      skipActiveFilter: true,
      ResultType: 'simple'
    });
    this.summary = result.data[0];
    this._totalMembersCount = this.summary.TotalMembersCount || 0;

    this.updateSummaryCards();
  }

  private updateSummaryCards(): void {
    this.summaryCards = [
      {
        id: 'total-members',
        value: `${this.summary.TotalMembersCount}`,
        label: this.summary.TotalMembersCount === 1 ? 'Member' : 'Members',
        icon: '👥',
        variant: 'primary',
        format: 'number'
      },
      {
        id: 'active-members',
        value: `${this.summary.TotalActiveMembersCount}`,
        label: 'Active Members',
        icon: '✅',
        variant: 'success',
        format: 'number'
      },
      {
        id: 'inactive-members',
        value: `${this.summary.TotalInactiveSuspendedCount}`,
        label: 'Inactive/Suspended',
        icon: '⏳',
        variant: 'warning',
        format: 'number'
      },
      {
        id: 'overdue-dues',
        value: `${this.summary.TotalDuesOverdueCount}`,
        label: 'Overdue Dues',
        icon: '💰',
        variant: 'info',
        format: 'number'
      }
    ];
  }
  */

  
  /**
   * Build committee access filter based on user's role and committee assignments
   * Chapter Admins see only their committee data, Staff see all data
   *
   * @param committeeColumnName - Name of the committee column in the SQL query (defaults to 'CommitteeID')
   * @returns SQL filter string for committee access, or empty string if no filter needed
   */
  buildCommitteeAccessFilter(committeeColumnName: string = 'CommitteeID'): string {
    try {
      // Get the active user for logging
      const activeUser = this.activeUserService.getActiveUser();
      if (!activeUser) {
        LogError('buildCommitteeAccessFilter: No active user found for committee filtering');
        return '';
      }

      // Check user role using UserAuthService - Staff users have no restrictions
      if (this.userAuthService.isStaff()) {
        LogStatus(`buildCommitteeAccessFilter: User ${activeUser.Name} has Staff role - no committee filtering applied`);
        return '';
      }

      // Check if user has no access at all
      if (!this.userAuthService.hasAccess()) {
        LogStatus(`buildCommitteeAccessFilter: User ${activeUser.Name} has no access - returning restrictive filter`);
        return `${committeeColumnName} = ''`;
      }

      // For Chapter Admins and other roles with access, apply committee access filtering
      const committeeFilter = this.userCommitteeAccessService.getCommitteeAccessFilter(committeeColumnName);

      if (committeeFilter) {
        const userRole = this.userAuthService.getUserRole();
        LogStatus(`buildCommitteeAccessFilter: Applied committee access filter for ${activeUser.Name} (${userRole}): ${committeeFilter}`);
      } else {
        LogStatus(`buildCommitteeAccessFilter: No committee access restrictions for ${activeUser.Name}`);
      }

      return committeeFilter;

    } catch (error) {
      LogError('buildCommitteeAccessFilter: Error building committee access filter', undefined, error);
      // Return restrictive filter on error for security
      return `${committeeColumnName} = ''`;
    }
  }
}